import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELDS as FF } from "@/lib/fields";
import { formatINR, banksLabel, fmtDate, fmtLastConv, fmtDateTimeIST, plural } from "@/lib/format";
import { StageChip } from "@/components/StageChip";
import { EditableField } from "@/components/EditableField";
import { NoteComposer } from "@/components/NoteComposer";
import { SuggestedMessages } from "@/components/SuggestedMessages";
import { CheckinCard } from "@/components/CheckinCard";
import { toast } from "sonner";
import { WaButton } from "@/components/WaButton"; // eslint-disable-line no-unused-vars
import { PartnerLine } from "@/components/PartnerLine";
import { SuggestedUpdates } from "@/components/AiSuggestions";
import { NpsBanner } from "@/components/Nps";
import { TruvaHouseLink } from "@/components/TruvaHouseLink";
import { telLink, relTime } from "@/lib/phone"; // eslint-disable-line no-unused-vars
import { AlertTriangle, Clock, Megaphone, Sparkles, MessageSquare, RefreshCw, CheckCircle2, Circle } from "lucide-react";
import { Info } from "@/components/Info";
import { NextActionField } from "@/components/NextActionField";

function ConsultCopies({ recordId }) {
  const [copies, setCopies] = useState([]);
  useEffect(() => { if (recordId) api.get(`/cases/${recordId}/consultation-copies`).then((r) => setCopies(r.data.copies || [])).catch(() => {}); }, [recordId]);
  if (!copies.length) return null;
  return copies.map((k) => (
    <a key={k.kind} href={k.link} target="_blank" rel="noreferrer" data-testid={`summary-consult-copy-${k.kind}`} title={`${k.kind} copied to the Shared Drive ${new Date(k.at).toLocaleDateString("en-IN")}`}
      className="flex items-center gap-1 text-xs text-[#159A50] border border-[#159A50]/30 rounded-lg px-2 py-1.5 hover:bg-[#159A50]/5"><CheckCircle2 className="w-3 h-3" /> Saved to Drive ✓ ({k.kind})</a>
  ));
}


const STAGE_SUGGESTION = {
  "Document Collection": "Collect documents from customer",
  "Buyer Approval": "Share verbal offers and get customer go-ahead",
  "Logged-In": "Follow up with bank on login",
  "IP/Sanction Issued": "Get customer go-ahead on IP",
  "Legal/Technical": "Track legal and technical reports",
  "Final Sanction OR DD copy": "Plan disbursement after registration",
};

export function CaseSummary({ c, recordId, saveField, selects, canEdit, reload, onGoTimeline, onGoWhatsApp, wa }) {
  const { user } = useAuth();
  const f = (id) => c?.fields?.[id];
  const [progress, setProgress] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [waSum, setWaSum] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [checkin, setCheckin] = useState(null);
  const [noteFocus, setNoteFocus] = useState(0);

  const loadCheckin = () => {
    api.get(`/cases/${recordId}/checkin`).then((r) => setCheckin(r.data)).catch(() => setCheckin(null));
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/cases/${recordId}/documents-checklist`).then((r) => !cancelled && setProgress(r.data.progress)).catch(() => {});
    const loadTimeline = async (attempt = 0) => {
      try {
        const r = await api.get(`/cases/${recordId}/timeline`);
        if (!cancelled) setTimeline((r.data.events || []).slice(0, 3));
      } catch {
        if (!cancelled && attempt < 2) setTimeout(() => loadTimeline(attempt + 1), 800);
      }
    };
    loadTimeline();
    const loadSum = async (attempt = 0) => {
      try {
        const r = await api.get(`/cases/${recordId}/whatsapp/summary`);
        if (cancelled) return;
        setWaSum(r.data);
        if (r.data?.updating && attempt < 6) setTimeout(() => loadSum(attempt + 1), 15000);
      } catch { /* noop */ }
    };
    loadSum();
    api.get(`/cases/${recordId}/checkin`).then((r) => !cancelled && setCheckin(r.data)).catch(() => !cancelled && setCheckin(null));
    return () => { cancelled = true; };
  }, [recordId]);

  const refreshSummary = async () => {
    setRefreshing(true);
    try { const r = await api.post(`/cases/${recordId}/whatsapp/summary/refresh`); setWaSum(r.data); toast.success("Summary refreshed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not refresh"); }
    finally { setRefreshing(false); }
  };
  const togglePromise = async (idx) => {
    try { await api.post(`/cases/${recordId}/whatsapp/promises/${idx}/toggle`); const r = await api.get(`/cases/${recordId}/whatsapp/summary`); setWaSum(r.data); }
    catch { toast.error("Failed"); }
  };

  const name = f(FF.BORROWER) || f(FF.NAME);
  const needsExtra = [];
  const stage = f(FF.LEAD_STAGE);
  const nextAction = f(FF.NEXT_ACTION);
  const waSuggested = waSum?.data?.suggested_next_action;
  // 7.9: only one next-action suggestion at a time — the WhatsApp one wins over the generic stage one
  const suggestion = !nextAction && !waSuggested && STAGE_SUGGESTION[stage];
  const lastConv = f(FF.LAST_CONVERSATION);
  const todayIso = new Date().toISOString().slice(0, 10);
  const fuOverdue = c.stage_group === "active" && f(FF.FOLLOW_UP_DATE) && String(f(FF.FOLLOW_UP_DATE)).slice(0, 10) < todayIso;
  const [askNext, setAskNext] = useState(false);
  const valueWarnings = [[FF.LOAN_AMOUNT, "Loan"], [FF.AGREEMENT_VALUE, "Agreement value"]]
    .filter(([fid]) => { const n = Number(f(fid)); return f(fid) != null && f(fid) !== "" && !Number.isNaN(n) && ((n > 0 && n < 100000) || n > 500000000); })
    .map(([fid, l]) => `${l} is ${formatINR(f(fid))}`);
  if (valueWarnings.length && !needsExtra.includes("value")) needsExtra.push("value");

  // 7.10: warn on unusual money values (< ₹1 L or > ₹50 Cr) before saving
  const guardedSave = async (fid, v, opts = {}) => {
    if ((fid === FF.LOAN_AMOUNT || fid === FF.AGREEMENT_VALUE) && v !== null && v !== "" && !Number.isNaN(Number(v))) {
      const n = Number(v);
      if ((n > 0 && n < 100000) || n > 500000000) {
        if (!window.confirm(`${formatINR(n)} looks unusual (expected between ₹1 L and ₹50 Cr). Save anyway?`)) return false;
      }
    }
    const r = await saveField(fid, v, opts);
    if (fid === FF.FOLLOW_UP_DATE && v && String(v).slice(0, 10) > todayIso && !nextAction) setAskNext(true);
    return r;
  };
  const lastConvIso = lastConv ? String(lastConv).slice(0, 10) : null;
  const lastConvIsToday = lastConvIso === todayIso;
  const [fuSpoke, setFuSpoke] = useState(false);   // follow-up-only change: default OFF
  const fuGuard = async (fid, v) => {
    if (v && lastConvIso && String(v).slice(0, 10) < lastConvIso) { toast.error(`Follow-up can't be before the last conversation (${fmtDate(lastConv)})`); return false; }
    return guardedSave(fid, v, { logConversation: fuSpoke && !lastConvIsToday });
  };

  const confirmConfidence = async () => {
    try { await api.post(`/cases/${recordId}/confidence/confirm`); toast.success("Confidence confirmed"); reload(true); }
    catch { toast.error("Failed"); }
  };
  const quickFollow = async (days) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    await guardedSave(FF.FOLLOW_UP_DATE, d.toISOString().slice(0, 10), { logConversation: fuSpoke && !lastConvIsToday });
  };

  const needs = [];
  if (c.needs_confidence) needs.push("Set RM Confidence & Expected Quarter");
  if (!nextAction && ["active"].includes(c.stage_group)) needs.push("Next Action is empty");
  if (needsExtra.includes("value")) needs.push("Loan / agreement value looks wrong — check the amount");
  if (c.stage_group === "active" && !f(FF.BORROWER_PROFILE)) needs.push("Set borrower profile (below)");

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-8 space-y-5">
        {/* 1. Summary line: stage + days in stage only (everything else lives in Key numbers) */}
        <div className="rounded-2xl border border-[#E2E2E2] bg-white px-5 py-3 shadow-sm flex items-center gap-3 flex-wrap" data-testid="summary-header">
          <StageChip stage={stage} />
          {c.days_in_stage != null ? <span className="text-xs text-[#6B7280]" data-testid="summary-days-in-stage">{plural(c.days_in_stage, "day")} in stage</span>
            : c.tat?.unknown_start ? <span className="text-xs text-[#92600a] font-medium" data-testid="summary-stage-start-unknown">Stage start date unknown</span> : null} <Info k="days_in_stage" />
          {fuOverdue && <span className="text-xs text-[#CA1212] font-medium" data-testid="summary-overdue">· follow-up overdue since {fmtDate(f(FF.FOLLOW_UP_DATE))}</span>}
        </div>

        {/* Post-consultation check-in banner (guided D1) */}
        {checkin?.eligible && (
          <CheckinCard
            cs={{ record_id: recordId, case_id: c.case_id, consult_name: checkin.consult_name, name, consultation_taken_by: checkin.consultation_taken_by }}
            selects={selects}
            embedded
            onDone={() => { setCheckin({ ...checkin, eligible: false }); reload(true); }}
          />
        )}

        {/* confidence stale banner */}
        {c.confidence_stale && (
          <div className="rounded-2xl border border-[#F5B71D]/50 bg-[#F5B71D]/10 p-3 flex items-center justify-between" data-testid="confidence-banner">
            <span className="text-sm text-[#92600a]">Still {f(FF.RM_CONFIDENCE)} for {f(FF.EXPECTED_DISB_QUARTER)}?</span>
            <div className="flex gap-2">
              <button onClick={confirmConfidence} data-testid="confidence-yes" className="px-3 py-1.5 rounded-lg bg-[#159A50] text-white text-xs font-medium">Yes</button>
            </div>
          </div>
        )}

        {/* 2. Now card */}
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm space-y-3" data-testid="summary-now">
          <h3 className="text-xs font-medium text-[#6B7280] uppercase" style={{ letterSpacing: "0.06em" }}>Now</h3>
          <NextActionField value={nextAction} stage={stage} editable={canEdit} lastConvIsToday={lastConvIsToday} onSave={(fid, v, opts) => { setAskNext(false); return saveField(fid, v, opts); }} testid="summary-next-action" />
          {askNext && !nextAction && <div className="text-xs text-[#92600a] bg-[#F5B71D]/10 border border-[#F5B71D]/40 rounded-lg px-3 py-1.5" data-testid="next-action-prompt">Follow-up set — what's the next action for that day? Add it above.</div>}
          {suggestion && canEdit && (
            <button onClick={() => saveField(FF.NEXT_ACTION, suggestion)} data-testid="next-action-suggest" className="flex items-center gap-1.5 text-xs text-[#FF4802] hover:underline">
              <Sparkles className="w-3.5 h-3.5" /> Use suggestion: "{suggestion}"
            </button>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <EditableField label="Follow Up Date" value={f(FF.FOLLOW_UP_DATE)} fieldId={FF.FOLLOW_UP_DATE} type="date" editable={canEdit} onSave={fuGuard} render={(v) => fuOverdue ? <span className="text-[#CA1212] font-semibold">{fmtDate(v)} · overdue</span> : fmtDate(v)} hint="follow_up" />
              {canEdit && (
                <div className="flex gap-1.5 mt-1 items-center flex-wrap" data-testid="summary-fu-quick">
                  {[["Tomorrow", 1], ["+2", 2], ["+3", 3]].map(([l, d]) => (
                    <button key={l} onClick={() => quickFollow(d)} className="px-2 py-0.5 rounded-full text-[11px] border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF6F38]">{l}</button>
                  ))}
                  {!lastConvIsToday && (
                    <label className="flex items-center gap-1 text-[11px] text-[#6B7280] cursor-pointer ml-1" data-testid="summary-fu-spoke-label">
                      <input type="checkbox" checked={fuSpoke} onChange={(e) => setFuSpoke(e.target.checked)} data-testid="summary-fu-spoke-toggle" className="accent-[#FF4802]" /> Spoke to the customer today?
                    </label>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase text-[#6B7280] flex items-center gap-1" style={{ letterSpacing: "0.06em" }}>Last Conversation <Info k="last_conv" /></div>
              <div className="text-sm text-[#333333] mt-2" data-testid="summary-last-conv">{lastConv ? fmtLastConv(lastConv) : "—"}</div>
              {canEdit && <button onClick={() => setNoteFocus((n) => n + 1)} data-testid="log-conversation" className="text-[11px] text-[#FF4802] hover:underline mt-1">Log a conversation</button>}
            </div>
          </div>
          {(f(FF.CONSULTATION_NOTES_LINK) || f(FF.CONSULTATION_RECORDING_LINK)) && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="summary-consult-links">
              {f(FF.CONSULTATION_NOTES_LINK) && (
                <a href={f(FF.CONSULTATION_NOTES_LINK)} target="_blank" rel="noreferrer" data-testid="summary-consult-notes"
                  className="flex items-center gap-1.5 text-xs text-[#38A2FF] border border-[#38A2FF]/30 rounded-lg px-3 py-1.5 hover:bg-[#38A2FF]/5">Consultation notes</a>
              )}
              {f(FF.CONSULTATION_RECORDING_LINK) && (
                <a href={f(FF.CONSULTATION_RECORDING_LINK)} target="_blank" rel="noreferrer" data-testid="summary-consult-recording"
                  className="flex items-center gap-1.5 text-xs text-[#8B46FF] border border-[#8B46FF]/30 rounded-lg px-3 py-1.5 hover:bg-[#8B46FF]/5">Recording</a>
              )}
              <ConsultCopies recordId={recordId || c?.id} />
            </div>
          )}
        </div>

        {/* 3. Key numbers */}
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="summary-numbers">
          <h3 className="text-xs font-medium text-[#6B7280] uppercase mb-3" style={{ letterSpacing: "0.06em" }}>Key numbers</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <EditableField label="Loan" value={f(FF.LOAN_AMOUNT)} fieldId={FF.LOAN_AMOUNT} type="number" editable={canEdit} onSave={guardedSave} render={(v) => formatINR(v)} testid="summary-loan" />
            <EditableField label="Agreement Value" value={f(FF.AGREEMENT_VALUE)} fieldId={FF.AGREEMENT_VALUE} type="number" editable={canEdit} onSave={guardedSave} render={(v) => formatINR(v)} testid="summary-agreement" />
            <EditableField label="Borrower Profile" value={f(FF.BORROWER_PROFILE)} fieldId={FF.BORROWER_PROFILE} type="select" options={selects[FF.BORROWER_PROFILE] || []} editable={canEdit} onSave={saveField} testid="summary-borrower-profile" hint="borrower_profile" />
            <EditableField label="Property Finalised" value={f(FF.PROPERTY_FINALISED)} fieldId={FF.PROPERTY_FINALISED} type="select" options={selects[FF.PROPERTY_FINALISED] || []} editable={canEdit} onSave={saveField} testid="summary-property-finalised" render={(v) => v || (f(FF.TRUVA_NONTRUVA) === "Yes" ? "Finalised (Truva)" : "—")} />
            <EditableField label="Truva / Out of Truva" value={f(FF.TRUVA_NONTRUVA)} fieldId={FF.TRUVA_NONTRUVA} type="select" options={selects[FF.TRUVA_NONTRUVA] || []} editable={canEdit} onSave={saveField} testid="summary-truva" render={(v) => v === "Yes" ? "Truva" : v === "No" || v === "Non Truva" ? "Out of Truva" : (v || "—")} />
            {valueWarnings.length > 0 && <div className="col-span-2 text-xs text-[#CA1212] bg-[#CA1212]/5 border border-[#CA1212]/30 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-testid="value-warning"><AlertTriangle className="w-3.5 h-3.5" /> {valueWarnings.join(" · ")} — expected between ₹1 L and ₹50 Cr. Did you mean lakhs or crores?</div>}
            <EditableField label="Bank(s)" value={f(FF.BANK_NAME)} fieldId={FF.BANK_NAME} type="multiselect" options={selects[FF.BANK_NAME] || []} editable={canEdit} onSave={saveField} render={(v) => banksLabel(v)} testid="summary-banks" />
            <EditableField label="Partner" value={f(FF.PARTNER_COMPANY)} fieldId={FF.PARTNER_COMPANY} type="select" options={selects[FF.PARTNER_COMPANY] || []} editable={canEdit} testid="summary-partner"
              onSave={(fid, v) => (window.confirm("This changes which partner can see this case. Continue?") ? saveField(fid, v) : false)} />
            {f(FF.PARTNER_COMPANY) && <EditableField label="Partner SPOC" value={f(FF.PARTNER_SPOC)} fieldId={FF.PARTNER_SPOC} type="select" options={selects[FF.PARTNER_SPOC] || []} editable={canEdit} testid="summary-partner-spoc"
              onSave={(fid, v) => (window.confirm("This changes which partner can see this case. Continue?") ? saveField(fid, v) : false)} />}
            <EditableField label="RM Confidence" value={f(FF.RM_CONFIDENCE)} fieldId={FF.RM_CONFIDENCE} type="select" options={selects[FF.RM_CONFIDENCE] || ["25%", "50%", "75%", "90%"]} editable={canEdit} onSave={saveField} hint="rm_confidence" />
            <EditableField label="Expected Quarter" value={f(FF.EXPECTED_DISB_QUARTER)} fieldId={FF.EXPECTED_DISB_QUARTER} type="select" options={selects[FF.EXPECTED_DISB_QUARTER] || []} editable={canEdit} onSave={saveField} hint="expected_quarter" />
            <EditableField label="Consultation Taken By" value={f(FF.CONSULTATION_TAKEN_BY)} fieldId={FF.CONSULTATION_TAKEN_BY} type="select" options={selects[FF.CONSULTATION_TAKEN_BY] || []} editable={canEdit} onSave={saveField} hint="consult_by" />
            {c.property_truva_name ? <TruvaHouseLink c={c} canEdit={canEdit} onSaved={() => reload(true)} /> : <><EditableField label="Property (Out of Truva)" value={f(FF.PROPERTY_NAME_OOT)} fieldId={FF.PROPERTY_NAME_OOT} editable={canEdit} onSave={saveField} testid="summary-property" />{f(FF.TRUVA_NONTRUVA) === "Truva" && <TruvaHouseLink c={c} canEdit={canEdit} onSaved={() => reload(true)} />}</>}
            <div className="col-span-2" data-testid="summary-lead-source"><div className="text-[11px] font-medium uppercase text-[#6B7280] flex items-center gap-1" style={{ letterSpacing: "0.06em" }}>Lead Source <Info k="lead_source" /></div><div className="text-sm text-[#333333] mt-1.5">{f(FF.LEAD_SOURCE) || "—"}{f("_attribution")?.utm_campaign || f("_attribution")?.ad_name ? <span className="text-[#6B7280]"> · {f("_attribution")?.utm_campaign || f("_attribution")?.ad_name}</span> : null}</div></div>
          </div>
        </div>

        {/* 4. Progress */}
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="summary-progress">
          <h3 className="text-xs font-medium text-[#6B7280] uppercase mb-2" style={{ letterSpacing: "0.06em" }}>Document progress <Info k="doc_progress" /></h3>
          <div className="flex flex-wrap gap-4 text-sm text-[#333333]">
            {progress && ["borrower", "coborrower", "company"].some((k) => progress[k]?.total > 0) ? (
              ["borrower", "coborrower", "company"].map((k) => progress[k] && progress[k].total > 0 && (
                <span key={k} className="capitalize">{k}: <b>{progress[k].done}/{progress[k].total}</b></span>
              ))
            ) : (
              <span className="text-[#6B7280]">Set borrower profile in Documents to track progress.</span>
            )}
          </div>
        </div>

        {/* 7. Note composer (quick action: add note) */}
        <NoteComposer recordId={recordId} onDone={() => reload(true)} focusSignal={noteFocus}
          consulted={["", "New Lead"].includes((c.fields?.[FF.LEAD_STAGE] || "").trim()) && c.fields?.[FF.CONSULTATION_STATUS] === "Completed"}
          followUpDate={c.fields?.[FF.FOLLOW_UP_DATE] || null} />
      </div>

      <div className="col-span-12 lg:col-span-4 space-y-5">
        {/* WhatsApp summary (G) + promises (H5) */}
        <div className="rounded-2xl border border-[#159A50]/30 bg-[#159A50]/5 p-4" data-testid="wa-summary-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-[#0f7d40] uppercase flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><MessageSquare className="w-3.5 h-3.5" /> WhatsApp summary <Info k="wa_summary" /></h3>
            {waSum?.updating && <span className="text-[11px] text-[#6B7280] flex items-center gap-1" data-testid="wa-summary-updating"><RefreshCw className="w-3 h-3 animate-spin" />Updating…</span>}
            <button onClick={refreshSummary} disabled={refreshing} data-testid="wa-summary-refresh" className="text-[11px] text-[#159A50] hover:underline flex items-center gap-1 disabled:opacity-50"><RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />Refresh</button>
          </div>
          {!waSum?.data ? <p className="text-xs text-[#6B7280]">No summary yet. Link a WhatsApp chat and refresh.</p> : (
            <div className="space-y-2">
              <p className="text-xs text-[#333333] whitespace-pre-wrap" data-testid="wa-summary-status">{waSum.data.status}</p>
              {waSum.data.suggested_next_action && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-[#6B7280] flex-1">Suggested: {waSum.data.suggested_next_action}</span>
                  {canEdit && <button onClick={() => saveField(FF.NEXT_ACTION, waSum.data.suggested_next_action)} data-testid="wa-use-next-action" className="text-[11px] text-[#FF4802] hover:underline whitespace-nowrap">Use as Next Action</button>}
                </div>
              )}
              {(waSum.data.we_promised || []).length > 0 && (
                <div className="pt-1" data-testid="wa-promises">
                  <div className="text-[11px] uppercase text-[#6B7280] mb-1" style={{ letterSpacing: "0.06em" }}>Promised</div>
                  {waSum.data.we_promised.map((p, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs mb-1 group">
                      <button onClick={() => togglePromise(i)} data-testid={`wa-promise-${i}`} className="flex items-start gap-1.5 text-left flex-1">
                        {p.done ? <CheckCircle2 className="w-3.5 h-3.5 text-[#159A50] flex-shrink-0 mt-0.5" /> : <Circle className="w-3.5 h-3.5 text-[#C5C4C4] flex-shrink-0 mt-0.5" />}
                        <span className={p.done ? "line-through text-[#C5C4C4]" : "text-[#333333]"}>{p.what}{p.by_when ? ` · ${p.by_when}` : ""}</span>
                      </button>
                      <button onClick={async () => { try { const r = await api.post(`/cases/${recordId}/whatsapp/promises/${i}/dismiss`); const g = await api.get(`/cases/${recordId}/whatsapp/summary`); setWaSum(g.data); toast.success("Removed — won't come back", { duration: 6000, action: { label: "Undo", onClick: async () => { await api.post(`/cases/${recordId}/whatsapp/promises/undismiss`, { what: r.data.what, by_when: r.data.by_when }); const g2 = await api.get(`/cases/${recordId}/whatsapp/summary`); setWaSum(g2.data); } } }); } catch { toast.error("Failed"); } }}
                        data-testid={`wa-promise-dismiss-${i}`} title="Not a promise" className="text-[10px] text-[#C5C4C4] hover:text-[#CA1212]">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {waSum.updated_at && <div className="text-[10px] text-[#C5C4C4]" title={fmtDateTimeIST(waSum.updated_at)} data-testid="wa-summary-updated">updated {relTime(waSum.updated_at)}</div>}
            </div>
          )}
        </div>

        {/* 5. Needs attention */}
        {needs.length > 0 && (
          <div className="rounded-2xl border border-[#CA1212]/30 bg-[#CA1212]/5 p-4" data-testid="summary-needs">
            <h3 className="text-xs font-medium text-[#CA1212] uppercase mb-2 flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><AlertTriangle className="w-3.5 h-3.5" /> Needs attention</h3>
            <ul className="space-y-1 text-sm text-[#333333]">
              {needs.map((n) => <li key={n} className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#CA1212]" /> {n}</li>)}
            </ul>
          </div>
        )}
        <NpsBanner recordId={recordId} />
        <SuggestedUpdates recordId={recordId} onApplied={() => reload(true)} />
        <PartnerLine recordId={recordId} />
        {(c.partner_updates || []).filter((u) => !u.acknowledged_at).length > 0 && (
          <div className="rounded-2xl border border-[#F5B71D]/40 bg-[#F5B71D]/10 p-4" data-testid="summary-partner-updates">
            <h3 className="text-xs font-medium text-[#92600a] uppercase mb-2 flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><Megaphone className="w-3.5 h-3.5" /> Partner updates</h3>
            {(c.partner_updates || []).filter((u) => !u.acknowledged_at).map((u) => <div key={u.id} className="text-xs text-[#333333]">{u.spoc_name}: {u.text}</div>)}
          </div>
        )}

        {/* 6. Recent activity */}
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm" data-testid="summary-recent">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-[#6B7280] uppercase flex items-center gap-1.5" style={{ letterSpacing: "0.06em" }}><Clock className="w-3.5 h-3.5" /> Recent activity</h3>
            <button onClick={onGoTimeline} data-testid="see-full-timeline" className="text-xs text-[#FF4802] hover:underline">See full timeline</button>
          </div>
          {timeline.length === 0 ? <p className="text-xs text-[#6B7280]">No activity yet.</p> : (
            <div className="space-y-2">
              {timeline.map((e, i) => (
                <div key={i} className="text-xs"><span className="text-[#333333]">{String(e.text).slice(0, 80)}</span><div className="text-[#C5C4C4]">{e.actor || ""}</div></div>
              ))}
            </div>
          )}        </div>

        {/* Suggested messages */}
        <SuggestedMessages c={c} recordId={recordId} onNoteAdded={() => reload(true)} />
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase text-[#6B7280]" style={{ letterSpacing: "0.06em" }}>{label}</div>
      <div className="text-sm text-[#333333] mt-1.5">{value}</div>
    </div>
  );
}
